// WebRTC Manager for voice calls
// Handles peer-to-peer audio connection via WebRTC
// Uses HTTP API for signaling instead of ActionCable

interface RTCConfig {
  iceServers: RTCIceServer[]
}

type ConnectionState = "idle" | "connecting" | "connected" | "disconnected"

type StateChangeCallback = (state: ConnectionState, remotePeerName?: string) => void

class WebRTCManager {
  private peerConnection: RTCPeerConnection | null = null
  private localStream: MediaStream | null = null
  private remoteStream: MediaStream | null = null
  private targetUserId: number | null = null
  private onStateChange: StateChangeCallback | null = null
  private connectionState: ConnectionState = "idle"
  private remotePeerName: string = ""
  private reconnectAttempts: number = 0
  private maxReconnectAttempts: number = 3
  private pendingCandidates: RTCIceCandidateInit[] = []

  // ICE servers with STUN and TURN for reliable connectivity
  private config: RTCConfig = {
    iceServers: [
      { urls: "stun:stun.l.google.com:19302" },
      { urls: "stun:stun1.l.google.com:19302" },
      { urls: "stun:stun2.l.google.com:19302" },
      { urls: "stun:stun3.l.google.com:19302" },
      { urls: "stun:stun4.l.google.com:19302" },
      // 무료 TURN 서버 (OpenRelay)
      {
        urls: "turn:openrelay.metered.ca:80",
        username: "openrelayproject",
        credential: "openrelayproject"
      },
      {
        urls: "turn:openrelay.metered.ca:443",
        username: "openrelayproject",
        credential: "openrelayproject"
      },
      {
        urls: "turn:openrelay.metered.ca:443?transport=tcp",
        username: "openrelayproject",
        credential: "openrelayproject"
      }
    ]
  }

  setOnStateChange(callback: StateChangeCallback) {
    this.onStateChange = callback
  }

  private updateState(state: ConnectionState, peerName?: string) {
    this.connectionState = state
    if (peerName) {
      this.remotePeerName = peerName
    }
    if (this.onStateChange) {
      this.onStateChange(state, this.remotePeerName)
    }
  }

  getConnectionState(): ConnectionState {
    return this.connectionState
  }

  getRemotePeerName(): string {
    return this.remotePeerName
  }

  // Get CSRF token for API calls
  private getCSRFToken(): string {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta?.getAttribute("content") || ""
  }

  // Send signaling data via HTTP API
  private async sendSignal(targetUserId: number, signalType: string, data: any): Promise<void> {
    try {
      await fetch("/voice/signal", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCSRFToken()
        },
        body: JSON.stringify({
          target_user_id: targetUserId,
          signal_type: signalType,
          data: data
        })
      })
    } catch (error) {
      console.error("[WebRTC] Failed to send signal:", error)
    }
  }

  // Get voice settings from localStorage
  private getVoiceSettings(): any {
    try {
      const settings = localStorage.getItem('voiceSettings')
      return settings ? JSON.parse(settings) : {}
    } catch {
      return {}
    }
  }

  // Get local audio stream with high quality settings from user preferences
  async getLocalStream(): Promise<MediaStream> {
    if (this.localStream) {
      return this.localStream
    }

    const settings = this.getVoiceSettings()

    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: settings.echoCancellation !== false,
          noiseSuppression: settings.noiseSuppression !== false,
          autoGainControl: settings.autoGainControl !== false,
          sampleRate: 48000,
          sampleSize: 16,
          channelCount: 1
        },
        video: false
      })
      console.log("[WebRTC] Got local audio stream with settings:", settings)
      return this.localStream
    } catch (error) {
      console.error("[WebRTC] Failed to get local stream:", error)
      throw new Error("마이크 접근 권한이 필요합니다")
    }
  }

  // Check if PTT mode is enabled
  isPTTEnabled(): boolean {
    const settings = this.getVoiceSettings()
    return settings.ptt === true
  }

  // Optimize audio codec for better quality
  private preferOpusCodec(description: RTCSessionDescriptionInit): RTCSessionDescriptionInit {
    if (!description.sdp) return description

    // Prefer Opus codec with high bitrate
    let sdp = description.sdp

    // Set Opus parameters for high quality
    sdp = sdp.replace(
      /a=fmtp:111 /g,
      'a=fmtp:111 maxaveragebitrate=128000;stereo=0;sprop-stereo=0;useinbandfec=1;'
    )

    return {
      type: description.type,
      sdp: sdp
    }
  }

  // Create and configure peer connection
  private createPeerConnection(): RTCPeerConnection {
    const pc = new RTCPeerConnection(this.config)

    // Handle ICE candidates
    pc.onicecandidate = (event) => {
      if (event.candidate && this.targetUserId) {
        console.log("[WebRTC] Sending ICE candidate")
        this.sendSignal(this.targetUserId, "ice-candidate", event.candidate.toJSON())
      }
    }

    // Handle connection state changes
    pc.onconnectionstatechange = () => {
      console.log("[WebRTC] Connection state:", pc.connectionState)
      switch (pc.connectionState) {
        case "connecting":
          this.updateState("connecting")
          break
        case "connected":
          this.updateState("connected")
          break
        case "disconnected":
        case "failed":
        case "closed":
          this.updateState("disconnected")
          break
      }
    }

    // Handle ICE connection state
    pc.oniceconnectionstatechange = () => {
      console.log("[WebRTC] ICE connection state:", pc.iceConnectionState)

      if (pc.iceConnectionState === "failed") {
        console.log("[WebRTC] ICE connection failed, attempting restart")
        this.handleIceFailure()
      } else if (pc.iceConnectionState === "disconnected") {
        // Wait a bit before considering it failed
        setTimeout(() => {
          if (pc.iceConnectionState === "disconnected") {
            console.log("[WebRTC] ICE still disconnected, attempting restart")
            this.handleIceFailure()
          }
        }, 3000)
      } else if (pc.iceConnectionState === "connected" || pc.iceConnectionState === "completed") {
        this.reconnectAttempts = 0
      }
    }

    // Handle ICE gathering state
    pc.onicegatheringstatechange = () => {
      console.log("[WebRTC] ICE gathering state:", pc.iceGatheringState)
    }

    // Handle remote track
    pc.ontrack = (event) => {
      console.log("[WebRTC] Received remote track")
      this.remoteStream = event.streams[0]
      this.playRemoteAudio()
    }

    return pc
  }

  // Start a call (caller side)
  async startCall(targetUserId: number, targetUserName: string): Promise<void> {
    console.log("[WebRTC] Starting call to user:", targetUserId)
    this.targetUserId = targetUserId
    this.remotePeerName = targetUserName
    this.updateState("connecting", targetUserName)

    try {
      // Get local audio
      const stream = await this.getLocalStream()

      // Create peer connection
      this.peerConnection = this.createPeerConnection()

      // Add local tracks
      stream.getTracks().forEach((track) => {
        this.peerConnection!.addTrack(track, stream)
      })

      // Create and send offer with optimized codec
      const offer = await this.peerConnection.createOffer()
      const optimizedOffer = this.preferOpusCodec(offer)
      await this.peerConnection.setLocalDescription(optimizedOffer)

      console.log("[WebRTC] Sending offer with optimized audio")
      await this.sendSignal(targetUserId, "offer", {
        type: optimizedOffer.type,
        sdp: optimizedOffer.sdp
      })
    } catch (error) {
      console.error("[WebRTC] Failed to start call:", error)
      this.endCall()
      throw error
    }
  }

  // Handle incoming offer (callee side)
  async handleOffer(
    offerData: any,
    callerId: number,
    callerName: string
  ): Promise<void> {
    console.log("[WebRTC] Handling offer from user:", callerId)
    this.targetUserId = callerId
    this.remotePeerName = callerName
    this.updateState("connecting", callerName)

    try {
      // Get local audio
      const stream = await this.getLocalStream()

      // Create peer connection
      this.peerConnection = this.createPeerConnection()

      // Add local tracks
      stream.getTracks().forEach((track) => {
        this.peerConnection!.addTrack(track, stream)
      })

      // Set remote description
      const offer: RTCSessionDescriptionInit = {
        type: offerData.type,
        sdp: offerData.sdp
      }
      await this.peerConnection.setRemoteDescription(new RTCSessionDescription(offer))

      // Process any buffered ICE candidates
      await this.processPendingCandidates()

      // Create and send answer with optimized codec
      const answer = await this.peerConnection.createAnswer()
      const optimizedAnswer = this.preferOpusCodec(answer)
      await this.peerConnection.setLocalDescription(optimizedAnswer)

      console.log("[WebRTC] Sending answer with optimized audio")
      await this.sendSignal(callerId, "answer", {
        type: optimizedAnswer.type,
        sdp: optimizedAnswer.sdp
      })
    } catch (error) {
      console.error("[WebRTC] Failed to handle offer:", error)
      this.endCall()
      throw error
    }
  }

  // Handle incoming answer (caller side)
  async handleAnswer(answerData: any): Promise<void> {
    console.log("[WebRTC] Handling answer")
    if (!this.peerConnection) {
      console.error("[WebRTC] No peer connection")
      return
    }

    try {
      const answer: RTCSessionDescriptionInit = {
        type: answerData.type,
        sdp: answerData.sdp
      }
      await this.peerConnection.setRemoteDescription(new RTCSessionDescription(answer))

      // Process any buffered ICE candidates
      await this.processPendingCandidates()
    } catch (error) {
      console.error("[WebRTC] Failed to handle answer:", error)
    }
  }

  // Handle incoming ICE candidate
  async handleIceCandidate(candidateData: any): Promise<void> {
    console.log("[WebRTC] Handling ICE candidate")

    // Buffer candidate if remote description not set yet
    if (!this.peerConnection || !this.peerConnection.remoteDescription) {
      console.log("[WebRTC] Buffering ICE candidate until remote description is set")
      this.pendingCandidates.push(candidateData)
      return
    }

    try {
      await this.peerConnection.addIceCandidate(new RTCIceCandidate(candidateData))
    } catch (error) {
      console.error("[WebRTC] Failed to add ICE candidate:", error)
    }
  }

  // Process buffered ICE candidates
  private async processPendingCandidates(): Promise<void> {
    if (!this.peerConnection || !this.peerConnection.remoteDescription) {
      return
    }

    console.log(`[WebRTC] Processing ${this.pendingCandidates.length} buffered candidates`)

    for (const candidate of this.pendingCandidates) {
      try {
        await this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate))
      } catch (error) {
        console.error("[WebRTC] Failed to add buffered ICE candidate:", error)
      }
    }

    this.pendingCandidates = []
  }

  // Play remote audio
  private playRemoteAudio(): void {
    let audioElement = document.getElementById("remote-audio") as HTMLAudioElement
    if (!audioElement) {
      audioElement = document.createElement("audio")
      audioElement.id = "remote-audio"
      audioElement.autoplay = true
      document.body.appendChild(audioElement)
    }

    if (this.remoteStream) {
      audioElement.srcObject = this.remoteStream
      // Respect speaker volume setting
      const speakerVolume = document.getElementById("speaker-volume") as HTMLInputElement
      if (speakerVolume) {
        audioElement.volume = parseInt(speakerVolume.value) / 100
      }
    }
  }

  // Handle ICE failure with reconnection attempt
  private async handleIceFailure(): Promise<void> {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log("[WebRTC] Max reconnect attempts reached, ending call")
      this.cleanup()
      return
    }

    this.reconnectAttempts++
    console.log(`[WebRTC] Reconnection attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts}`)

    if (this.peerConnection) {
      try {
        // Try ICE restart
        const offer = await this.peerConnection.createOffer({ iceRestart: true })
        const optimizedOffer = this.preferOpusCodec(offer)
        await this.peerConnection.setLocalDescription(optimizedOffer)

        if (this.targetUserId) {
          await this.sendSignal(this.targetUserId, "offer", {
            type: optimizedOffer.type,
            sdp: optimizedOffer.sdp,
            iceRestart: true
          })
        }
      } catch (error) {
        console.error("[WebRTC] ICE restart failed:", error)
      }
    }
  }

  // End call and cleanup
  endCall(): void {
    console.log("[WebRTC] Ending call")

    // Notify the other party
    if (this.targetUserId) {
      this.sendSignal(this.targetUserId, "hangup", {})
    }

    this.cleanup()
  }

  // Handle remote hangup
  handleHangup(): void {
    console.log("[WebRTC] Remote party hung up")
    this.cleanup()
  }

  // Cleanup resources
  private cleanup(): void {
    // Stop local tracks
    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => track.stop())
      this.localStream = null
    }

    // Close peer connection
    if (this.peerConnection) {
      this.peerConnection.close()
      this.peerConnection = null
    }

    // Remove remote audio element
    const audioElement = document.getElementById("remote-audio")
    if (audioElement) {
      audioElement.remove()
    }

    this.targetUserId = null
    this.remoteStream = null
    this.pendingCandidates = []
    this.reconnectAttempts = 0
    this.updateState("idle")
  }

  // Mute/unmute local audio
  setMuted(muted: boolean): void {
    if (this.localStream) {
      this.localStream.getAudioTracks().forEach((track) => {
        track.enabled = !muted
      })
      console.log("[WebRTC] Muted:", muted)
    }
  }

  // Set speaker volume
  setSpeakerVolume(volume: number): void {
    const audioElement = document.getElementById("remote-audio") as HTMLAudioElement
    if (audioElement) {
      audioElement.volume = volume / 100
    }
  }

  // Check if in call
  isInCall(): boolean {
    return this.peerConnection !== null && this.connectionState !== "idle"
  }
}

// Global instance
const webRTCManager = new WebRTCManager()

// Make accessible globally
;(window as any).webRTCManager = webRTCManager

export default webRTCManager
